import type {
  DsdEditorMappingReviewJudgement,
  DsdEditorParagraphSegment,
  DsdEditorValueOccurrence,
} from "@/apis/dsd-editor/route";
import { DsdEditorDocumentContent } from "../document-content";
import { PrevNextControl } from "@/toggle-ui-kit/prev-next-control";
import { cn } from "@/lib/utils";
import { alignHorizontalScrollersToCell } from "../use-document-scroll";
import { IconButton } from "@/toggle-ui-kit/icon-button";
import type { DsdEditorParsedItem } from "@/utils/dsd-editor/document-items";
import { X } from "lucide-react";
import { BetaFeatureOnly } from "@/components/beta-feature";
import { createPortal } from "react-dom";
import {
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useModalStore } from "@/stores/modal";
import { useDsdEditorCellSelection } from "../use-dsd-editor-cell-selection";
import { cellSelector } from "../cell-selector";
import { DSD_REVIEW_COPY, DSD_REVIEW_STATUS_LABELS } from "../copy";
import { EVIDENCE_GAP_PX, EVIDENCE_HEIGHT_PX } from "./evidence-layout";
import { getDsdReviewEvidenceView } from "./evidence-view";
import { useStickyHeaderRows } from "./use-sticky-header-rows";
import { isTypingTarget } from "../is-typing-target";
import {
  DsdReviewJudgementStatus,
  DsdReviewMatchIndicator,
  DsdReviewTagChip,
} from "../review-indicators";

const MIN_WIDTH = 740;
const MAX_WIDTH = 940;
/** 창 높이. 내용이 짧아도 이 높이를 잡는다 */
const HEIGHT = EVIDENCE_HEIGHT_PX;
/** 창 아래가 화면 밖으로 이 비율까지 나가는 것은 허용, 그 이상이면 열지 않거나 닫는다 */
const MAX_OVERFLOW_RATIO = 0.2;
/** 화면 가장자리·선택 셀과의 간격 */
const GAP = EVIDENCE_GAP_PX;
/** 닫힐 때 작아지는 시간 — 아래 className 의 duration-100 과 같아야 한다. 이 시간이 지난 뒤 실제로 사라진다 (열림은 duration-150) */
const CLOSE_MS = 100;

const EMPTY_CELL_IDS = new Set<string>();
const EMPTY_STATUS_BY_CELL_ID = new Map<string, never>();

/** 조립이 정하는 근거 창 내용. 뷰가 여기에 문서 참조를 붙여 창을 그린다 */
export type DsdReviewEvidence = {
  /** 창이 붙는 셀 (선택된 본문 셀) */
  anchorCellId: string;
  /** 보여줄 후보 (주석 쪽 값 위치) */
  occurrence: DsdEditorValueOccurrence;
  /** 행·셀 색 — 후보 카드와 같은 상태 톤 */
  tone: "positive" | "caution" | "negative";
  index: number;
  total: number;
  /** false 면 표 대신 "판단 근거를 찾지 못했습니다" 안내 (값도 계정명도 못 맞춘 청크 쌍) */
  hasEvidence: boolean;
  /** 머리글의 검토 버튼 — 검토 쌍이 있는 후보에만. 패널 카드의 원형·사유와 같은 검토 모달 */
  onReview: (() => void) | null;
  /** 머리글의 후보 요약 — 패널 카드와 같은 판정(조립이 카드 모델에서 넘긴다) */
  summary: DsdReviewEvidenceSummary;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
};

export type DsdReviewEvidenceSummary = {
  /** 주석 관계 칩 (참조 주석·다른 주석·본문). 모양·색은 패널 카드 칩과 같다(tagToneOf). 검토 완료여도 흐리게 하지 않는다 */
  tagLabel: string;
  tagTone: DsdReviewEvidence["tone"];
  /** 일치 표시 — 일치면 초록 ✓✓, 불일치면 주황 ⊘ */
  matchLabel: string;
  matchPositive: boolean;
  /** 저장된 판단. 있으면 상태 표시와 함께 버튼이 "검토 사유 수정" */
  judgement: DsdEditorMappingReviewJudgement | null;
};

type Frame = { top: number; left: number; width: number; height: number };

/**
 * 창의 자리. 셀 하단 + 16. 가로는 문서 영역 가운데, 740~940 — 문서 영역이 좁으면 왼쪽 목차 위로 나가
 * 폭을 확보하되 오른쪽 패널은 덮지 않는다(패널까지 740 이 안 되면 그때만 패널을 덮는다)
 */
const getFrame = (
  container: HTMLElement,
  anchor: HTMLElement,
  height: number,
): Frame => {
  const c = container.getBoundingClientRect();
  const a = anchor.getBoundingClientRect();
  const width = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, c.right - GAP * 2));
  const centered = c.left + c.width / 2 - width / 2;
  return {
    top: a.bottom + GAP,
    left: Math.max(GAP, Math.min(centered, c.right - GAP - width)),
    width,
    height,
  };
};

/** 창 아래가 화면 아래(여백 16)를 넘는 양 */
const overflowOf = (frame: Frame) =>
  frame.top + frame.height - (window.innerHeight - GAP);

const isInside = (a: DOMRect, c: DOMRect) =>
  a.top >= c.top && a.bottom <= c.bottom;

/** 버튼·링크·입력 위에서의 Enter 는 그 요소의 것 — 전역 Enter 가 겹치면 클릭과 함께 두 번 동작한다 */
const isInteractiveTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (isTypingTarget(target) ||
    target.closest(
      'button, a, [role="button"], [role="radio"], [role="switch"]',
    ) !== null);

/** 이 시간 안에 scroll 이벤트가 없으면 스크롤이 시작되지 않은 것 (한 프레임 남짓) */
const SCROLL_START_GRACE_MS = 80;
/** 마지막 scroll 이벤트 뒤 이 시간 동안 조용하면 끝난 것 — 부드러운 스크롤은 매 프레임 이벤트를 낸다 */
const SCROLL_IDLE_MS = 120;

/**
 * 진행 중인 문서 스크롤(셀 선택이 요청한 부드러운 스크롤)이 끝날 때까지.
 * 끝은 scrollend 로 알고, scrollend 가 없는 브라우저에서는 "scroll 이벤트가 잠시 끊김"으로 본다 —
 * 스크롤 길이를 짐작하지 않는다. 창은 스스로 스크롤하지 않는다
 */
const afterPendingScroll = (container: HTMLElement) =>
  new Promise<void>((resolve) => {
    let timer = setTimeout(done, SCROLL_START_GRACE_MS);
    const onScroll = () => {
      clearTimeout(timer);
      timer = setTimeout(done, SCROLL_IDLE_MS);
    };
    container.addEventListener("scroll", onScroll, { passive: true });
    container.addEventListener("scrollend", done, { once: true });
    function done() {
      clearTimeout(timer);
      container.removeEventListener("scroll", onScroll);
      container.removeEventListener("scrollend", done);
      resolve();
    }
  });

/**
 * 선택 셀 아래에 뜨는 근거 창. 화면 고정, 문서와 같이 스크롤되지 않고 셀을 따라 자리만 옮긴다.
 *
 * 열 때: 문서 스크롤은 셀 선택(또는 근거 확인)이 요청한 한 번뿐이다 — 30% 위치에 창 자리(EVIDENCE_RESERVE_BELOW_PX)까지
 * 계산된 스크롤. 창은 바로 셀 아래에 나타나 스크롤을 따라 움직이고, 스크롤이 끝난 뒤에 "화면 밖 20% 초과면 닫힘"을
 * 판정한다(움직이는 동안은 판정하지 않는다). 높이는 내용과 무관하게 600.
 * 열려 있는 동안: 셀을 따라 움직인다. 창 아래가 20% 넘게 나가거나 셀이 문서 영역 밖으로 나가면 닫는다. 높이는 줄이지 않는다.
 */
export const DsdReviewEvidencePopover = ({
  scrollContainerRef,
  itemsBySection,
  matchedParagraphSegments,
  anchorCellId,
  occurrence,
  tone,
  index,
  total,
  hasEvidence,
  onReview,
  summary,
  onPrev,
  onNext,
  onClose,
  dismissed = false,
  onExited,
}: DsdReviewEvidence & {
  /** 밖에서 내용이 사라졌다(선택 해제 등) — 닫힘 애니메이션을 재생한 뒤 onExited 를 부른다 */
  dismissed?: boolean;
  /** 닫힘 애니메이션이 끝나 화면에서 완전히 사라졌을 때 */
  onExited?: () => void;
  /** 본문 스크롤 영역 — 기준 셀 위치와 가로 범위를 여기서 읽는다 */
  scrollContainerRef: RefObject<HTMLDivElement>;
  itemsBySection: ReadonlyMap<string, DsdEditorParsedItem[]>;
  /** 문장 속 값을 값 세그먼트로 그리기 위한 매칭 문단 (본문과 같은 맵) */
  matchedParagraphSegments?: ReadonlyMap<string, DsdEditorParagraphSegment[]>;
}) => {
  const copy = DSD_REVIEW_COPY.evidence;
  const rootRef = useRef<HTMLElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  // DSD 표의 기본 동작(드래그 선택·Ctrl+C) — 창은 본문 스크롤 컨테이너 밖(body 포털)이라 본문의 위임이 닿지 않는다
  useDsdEditorCellSelection(bodyRef);
  // 자리를 잡으면 커지며 나타나고, 닫을 땐 작아진 뒤 사라진다 (모든 닫기 경로가 requestClose 를 지난다)
  const [phase, setPhase] = useState<"placing" | "open" | "closing">("placing");
  const onCloseRef = useRef({ onClose, onExited });
  onCloseRef.current = { onClose, onExited };
  const requestClose = useCallback(() => {
    setPhase((current) => {
      if (current === "closing") return current;
      setTimeout(() => {
        onCloseRef.current.onClose();
        onCloseRef.current.onExited?.();
      }, CLOSE_MS);
      return "closing";
    });
  }, []);
  useEffect(() => {
    if (dismissed) requestClose();
  }, [dismissed, requestClose]);
  const callbacks = useRef({ onPrev, onNext, onReview, onClose: requestClose });
  callbacks.current = { onPrev, onNext, onReview, onClose: requestClose };

  const view = useMemo(
    () => getDsdReviewEvidenceView(itemsBySection, occurrence),
    [itemsBySection, occurrence],
  );
  // 문장 속 값(문단 후보)은 값 세그먼트의 후보색 + 선택 테두리로 강조한다 — 표 셀은 evidence 톤으로
  const isParagraphEvidence = view.rowCellIds.size === 0;
  const candidateStatusByCellId = useMemo(
    () => new Map([...view.cellIds].map((id) => [id, tone] as const)),
    [view.cellIds, tone],
  );
  // 열 머리글 행을 창 위쪽에 고정 (스프레드시트의 틀 고정)
  useStickyHeaderRows(bodyRef, view);

  // 근거 셀이 창 안에 안 보이면 세로·가로로 스크롤해 보이게 (보이면 건드리지 않는다). 창 높이가 정해진 뒤에 부른다
  const revealCell = useCallback(() => {
    const body = bodyRef.current;
    if (!body) return;
    const cell = [...view.cellIds]
      .map((id) => body.querySelector(cellSelector(id)))
      .find((el): el is HTMLElement => el instanceof HTMLElement);
    if (!cell) return;
    const b = body.getBoundingClientRect();
    const r = cell.getBoundingClientRect();
    if (r.top < b.top || r.bottom > b.bottom) {
      body.scrollTop += r.top - b.top - b.height / 2 + r.height / 2;
    }
    // 창 본문 자신이 가로 스크롤러다 — root 를 그 바깥(창)으로 줘야 본문까지 맞춘다
    alignHorizontalScrollersToCell(rootRef.current ?? body, cell);
  }, [view.cellIds]);

  // 자리 잡기. 창은 body 포털이라 부모 ref 는 layout effect 시점에 붙어 있다.
  // 후보를 넘기면(occurrence) 자리만 다시 잡고 문서는 다시 스크롤하지 않는다
  useLayoutEffect(() => {
    // 후보가 바뀌면 창 안 스크롤은 처음부터 — 보여줄 셀이 없는 후보(청크 쌍)는 아래 revealCell 이 안 움직여서
    // 이전 후보에서 내려간 위치가 그대로 남는다
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
    const container = scrollContainerRef.current;
    const root = rootRef.current;
    if (!container || !root) return;
    const anchor = container.querySelector(cellSelector(anchorCellId));
    if (!(anchor instanceof HTMLElement)) return;

    const height = HEIGHT;
    const maxOverflow = height * MAX_OVERFLOW_RATIO;
    const apply = (frame: Frame) => {
      root.style.top = `${frame.top}px`;
      root.style.left = `${frame.left}px`;
      root.style.width = `${frame.width}px`;
      root.style.height = `${frame.height}px`;
      root.style.visibility = "visible";
      setPhase((current) => (current === "placing" ? "open" : current));
    };
    let cancelled = false;
    const listeners: Array<() => void> = [];
    // 선택이 요청한 스크롤이 끝날 때까지는 닫힘 판정을 미룬다 (움직이는 동안 잠깐 밖으로 나가는 것은 정상)
    let settling = true;
    const shouldClose = () => {
      const c = container.getBoundingClientRect();
      const a = anchor.getBoundingClientRect();
      if (!isInside(a, c)) return true;
      return overflowOf(getFrame(container, anchor, height)) > maxOverflow;
    };
    {
      apply(getFrame(container, anchor, height));
      // 높이가 정해진 뒤에야 창 본문이 스크롤 가능해진다 — 그때 근거 셀을 보이게.
      // 표는 마운트 뒤에도 열 너비가 바뀐다(잘린 숫자 보정 등) — 잠시 동안 크기 변화마다 다시 맞춘다
      revealCell();
      const tables = bodyRef.current?.querySelectorAll("table") ?? [];
      if (tables.length > 0 && typeof ResizeObserver !== "undefined") {
        const contentObserver = new ResizeObserver(() => revealCell());
        tables.forEach((table) => contentObserver.observe(table));
        const stop = setTimeout(() => contentObserver.disconnect(), 1000);
        listeners.push(() => {
          clearTimeout(stop);
          contentObserver.disconnect();
        });
      }
      // 열려 있는 동안: 셀을 따라 움직이고, 스크롤이 끝난 뒤부터 닫힘 판정
      const update = () => {
        apply(getFrame(container, anchor, height));
        if (!settling && shouldClose()) callbacks.current.onClose();
      };
      container.addEventListener("scroll", update, { passive: true });
      window.addEventListener("resize", update);
      // 오른쪽 패널·목차가 열리고 닫혀도 문서 영역 폭이 바뀐다
      const observer =
        typeof ResizeObserver === "undefined"
          ? null
          : new ResizeObserver(update);
      observer?.observe(container);
      listeners.push(() => {
        container.removeEventListener("scroll", update);
        window.removeEventListener("resize", update);
        observer?.disconnect();
      });
    }
    void afterPendingScroll(container).then(() => {
      if (cancelled) return;
      settling = false;
      if (shouldClose()) callbacks.current.onClose();
    });
    return () => {
      cancelled = true;
      listeners.forEach((off) => off());
    };
  }, [scrollContainerRef, anchorCellId, occurrence, revealCell]);

  // 창 밖 클릭: 목차·상단 바·오른쪽 패널 등은 창만 닫는다. 문서 영역 안은 기존 규칙에 맡긴다 —
  // 다른 셀이면 그 셀로 옮겨 가고, 빈 곳·클릭 안 되는 셀이면 선택이 풀리며 창도 같이 닫힌다.
  // 패널의 "근거 확인"(data-keep-evidence)은 창을 여는 버튼이라 제외
  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      // 검토 모달이 떠 있는 동안의 클릭(어두운 배경 포함)은 모달의 것 — 보던 창은 그대로 둔다
      if (useModalStore.getState().modals.stack.length > 0) return;
      if (rootRef.current?.contains(target)) return;
      if (scrollContainerRef.current?.contains(target)) return;
      if (target instanceof Element && target.closest("[data-keep-evidence]"))
        return;
      callbacks.current.onClose();
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", onPointerDown, true);
  }, [scrollContainerRef]);

  // Esc 닫기, ←/→ 이전·다음 후보, Enter 검토 모달(미검토 후보일 때) — 입력 중이 아닐 때, 위에 스택 모달이 없을 때
  // (검토 모달의 Esc·Enter 가 이 창까지 닿지 않게). Enter 는 키를 누르고 있는 반복(repeat)을 무시한다 —
  // 모달이 열린 직후 반복 keydown 이 모달의 Enter 로 들어가 기본 판단으로 바로 저장돼 버린다
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) return;
      if (useModalStore.getState().modals.stack.length > 0) return;
      if (event.key === "Escape") callbacks.current.onClose();
      else if (event.key === "ArrowLeft") callbacks.current.onPrev();
      else if (event.key === "ArrowRight") callbacks.current.onNext();
      else if (event.key === "Enter") {
        const { onReview } = callbacks.current;
        if (!onReview || event.repeat || isInteractiveTarget(event.target))
          return;
        onReview();
      } else return;
      event.preventDefault();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return createPortal(
    <section
      ref={rootRef}
      role="dialog"
      aria-label={copy.title}
      style={{ visibility: "hidden" }}
      // z-40: 목차(z-30, dsd-review-document-view) 위, 스택 모달(z-50) 아래 — 고정값이라 모달이 뜨는 순간에도 순서가 어긋나지 않는다
      // (모달 여부를 구독해 60→40 으로 바꾸던 때는 모달이 창 뒤에서 1~2 프레임 먼저 보였다). 상단 바·파일명 줄 위로는 올라오지 않는다(셀이 문서 영역을 벗어나면 닫힘)
      className={cn(
        "fixed z-40 flex origin-top flex-col overflow-hidden rounded-[12px] bg-background-base shadow-xlarge ring-1 ring-line-normal fill-mode-forwards ease-out",
        phase === "open" && "animate-in fade-in-0 zoom-in-95 duration-150",
        phase === "closing" &&
          "animate-out fade-out-0 zoom-out-95 duration-100",
      )}
    >
      <header className="flex h-[42px] shrink-0 items-center justify-between border-b border-line-normal px-3">
        <h3 className="label-2-semibold text-label-strong">{copy.title}</h3>
        <IconButton
          variant="transparent"
          size={28}
          icon={<X />}
          aria-label={copy.close}
          onClick={requestClose}
        />
      </header>
      {/* 후보 요약 (Figma 7975-522730 외) — 제목·주석 관계 칩·일치 표시 / 판단 상태·검토 버튼·이전다음 */}
      <div className="flex shrink-0 items-center justify-between gap-3 px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <h4 className="truncate body-2-normal-medium text-label-normal">
            {occurrence.chunkTitle || occurrence.chunkId}
          </h4>
          <DsdReviewTagChip label={summary.tagLabel} tone={summary.tagTone} />
          <DsdReviewMatchIndicator
            label={summary.matchLabel}
            positive={summary.matchPositive}
          />
        </div>
        <div className="flex h-10 shrink-0 items-center gap-4">
          {summary.judgement && (
            <DsdReviewJudgementStatus judgement={summary.judgement} />
          )}
          {onReview && (
            // 베타 표시용 — 끄는 것은 조립부가 onReview 를 null 로 넘겨서 (버튼·Enter 함께)
            <BetaFeatureOnly>
              <button
                type="button"
                onClick={onReview}
                className="h-8 rounded-[6px] bg-line-alternative px-2.5 label-1-normal-medium text-label-normal transition-colors hover:bg-decorate-neutral/12"
              >
                {summary.judgement ? copy.editReview : copy.review}
              </button>
            </BetaFeatureOnly>
          )}
          <PrevNextControl
            index={index}
            total={total}
            prevLabel={copy.prev}
            nextLabel={copy.next}
            onPrev={onPrev}
            onNext={onNext}
          />
        </div>
      </div>
      {/* 창 본문 하나가 가로·세로를 다 스크롤한다 — 표의 가로 스크롤 영역(Radix)과 그 위 클립을 여기서는 풀어 둔다.
          그 영역이 스크롤 컨테이너로 남아 있으면 머리글의 sticky 가 거기에 붙어 창 세로 스크롤을 따라가지 않는다.
          머리글 셀은 sticky(위치는 use-sticky-header-rows 가 잰 --sticky-top), 배경 없는 셀은 흰 바탕으로 본문을 가린다.
          고정된 동안은 그림자로 표시한다 */}
      <div
        ref={bodyRef}
        // overscroll-none: 끝에서 더 당겨도 뒤 문서로 스크롤이 넘어가지 않고 macOS 고무줄 튕김도 없다 (문서 영역과 같은 처방)
        className={cn(
          "min-h-0 flex-1 overflow-auto overscroll-none px-5",
          // 문서 프레임·표 영역의 overflow-clip 도 풀어 넓은 표의 폭이 본문 가로 스크롤로 이어지게
          "[&_.overflow-clip]:!overflow-visible [&_[data-radix-scroll-area-viewport]]:!overflow-visible",
          "[&_[data-table-scroll-area-root]>div:has([data-table-horizontal-scrollbar])]:hidden",
          // 넓은 표를 끝까지 밀었을 때도 왼쪽(본문 px-5 + 프레임 px-3)과 같은 여백이 남게 — 본문의 오른쪽 안쪽 여백은 넘친 폭에 붙지 않아서,
          // 표를 감싸는 상자(Radix 뷰포트 안쪽)를 표 폭에 맞추고(min-width 100% 해제) 그 안에 여백을 준다. 프레임에 들어가는 표는 가로 스크롤이 생기지 않는다
          "[&_[data-radix-scroll-area-viewport]>div]:!min-w-0 [&_[data-radix-scroll-area-viewport]>div]:pr-8",
          "[&_tr[data-header-row]>*]:sticky [&_tr[data-header-row]>*]:top-[var(--sticky-top,0px)] [&_tr[data-header-row]>*]:z-10",
          "[&_tr[data-header-row]>*:not([class*='bg-']):not([style*='background'])]:bg-background-base",
          // 머리글이 붙어 있는 동안(표 위쪽이 가려짐) 머리글 맨 아래에 닿는 셀 전부에 그림자 — 고정된 상태라는 표시
          "[&_table[data-header-stuck]_[data-header-bottom]]:shadow-base",
        )}
      >
        {!hasEvidence ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 pb-5 text-center">
            <p className="headline-2-semibold text-label-normal">
              {copy.noEvidenceTitle}
            </p>
            <p className="whitespace-pre-line body-2-normal-regular text-label-alternative">
              {copy.noEvidenceBody}
            </p>
          </div>
        ) : (
          <div className="pb-5">
            <DsdEditorDocumentContent
              sections={view.sections}
              itemsBySection={view.itemsBySection}
              selectedCellId={
                isParagraphEvidence ? ([...view.cellIds][0] ?? null) : null
              }
              // 행 선택 결과는 행 전체(톤 100), 값 셀은 톤 200 채움 + 500 테두리
              noteReferenceReview={{
                linkedCellIds: EMPTY_CELL_IDS,
                statusByCellId: EMPTY_STATUS_BY_CELL_ID,
                statusLabels: DSD_REVIEW_STATUS_LABELS,
                candidateStatusByCellId,
                matchedParagraphSegments,
                evidenceRowCellIds: view.rowCellIds,
                evidencePreviewCellIds: view.cellIds,
                evidencePreviewTone: `match-${tone}`,
              }}
              cellDomIdPrefix="dsd-editor-evidence-cell"
              showSectionTitle={false}
              frameClassName="border-0"
            />
          </div>
        )}
      </div>
    </section>,
    document.body,
  );
};

/**
 * 근거 창의 등장/퇴장 관리. 내용(evidence)이 사라져도 마지막 내용을 붙잡고 닫힘 애니메이션을 끝낸 뒤 내린다 —
 * 선택 해제처럼 밖에서 내용이 없어지는 경우도 X·Esc 와 같은 모양으로 사라지게
 */
export const DsdReviewEvidencePresence = ({
  evidence,
  ...rest
}: {
  evidence: DsdReviewEvidence | null;
  scrollContainerRef: RefObject<HTMLDivElement>;
  itemsBySection: ReadonlyMap<string, DsdEditorParsedItem[]>;
  matchedParagraphSegments?: ReadonlyMap<string, DsdEditorParagraphSegment[]>;
}) => {
  const [retained, setRetained] = useState(evidence);
  useEffect(() => {
    if (evidence) setRetained(evidence);
  }, [evidence]);
  const shown = evidence ?? retained;
  if (!shown) return null;
  return (
    <DsdReviewEvidencePopover
      {...rest}
      {...shown}
      dismissed={evidence === null}
      onExited={() => setRetained(null)}
    />
  );
};
